import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { CommissionCalculator } from "@/components/CommissionCalculator";
import { useSeo } from "@/hooks/useSeo";
import {
  IconAmbCode, IconAmbShare, IconAmbForm, IconAmbCoins,
  IconAmbDashboard, IconAmbSeal, IconCopy, IconCheck,
} from "@/components/BrandGraphics";

const STEPS = [
  { Icon: IconAmbCode, title: "Vous recevez un code unique", text: "À votre inscription, un code ambassadeur personnel est généré (ex : PP-THIBAUT-01)." },
  { Icon: IconAmbShare, title: "Vous recommandez Plan Permis", text: "Partagez votre code aux personnes qui cherchent une auto-école." },
  { Icon: IconAmbForm, title: "Le candidat renseigne le code", text: "Il indique votre code lors de sa demande sur Plan Permis." },
  { Icon: IconAmbCoins, title: "Vous êtes rémunéré à l'inscription", text: "Commission versée uniquement quand le candidat s'inscrit réellement dans une auto-école partenaire." },
];

export default function Ambassadeur() {
  const navigate = useNavigate();
  useSeo({
    title: "Devenez ambassadeur Plan Permis — gagnez jusqu'à 50 € par inscription",
    description:
      "Recommandez Plan Permis et gagnez une commission pour chaque inscription validée en auto-école partenaire. 25 € à 50 € par inscription. Sans engagement.",
  });

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    payment_info: "",
    type: "particulier",
    accepted_terms: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Merci de renseigner votre prénom, nom et email.");
      return;
    }
    if (!form.accepted_terms) {
      toast.error("Vous devez accepter les conditions du programme ambassadeur.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ambassadors/register", form);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier le code.");
    }
  };

  const inputCls =
    "w-full rounded-lg border border-neutral-300 bg-white px-fib-4 py-fib-3 outline-none focus:border-brand transition-colors";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-fib-4 sm:px-12">
        <Link to="/" data-testid="ambassador-logo-link">
          <Logo className="h-12" />
        </Link>
        <Link
          to="/ambassadeur/dashboard"
          data-testid="ambassador-dashboard-link"
          className="inline-flex items-center gap-fib-2 rounded-full border border-neutral-300 px-fib-4 py-fib-2 text-gr-sm font-semibold text-neutral-700 transition-colors hover:border-brand hover:text-brand"
        >
          <IconAmbDashboard className="h-4 w-4" />
          <span className="hidden sm:inline">Mon tableau de bord</span>
        </Link>
      </header>

      {/* Hero + calculator */}
      <section className="bg-gradient-to-br from-brand to-brand-active px-6 py-fib-7 text-white sm:px-12">
        <div className="mx-auto grid max-w-6xl items-center gap-fib-6 lg:grid-cols-2">
          <div>
            <span className="inline-block rounded-full bg-white/15 px-fib-4 py-fib-1 text-gr-xs font-bold uppercase tracking-[0.2em]">
              Programme ambassadeurs
            </span>
            <h1 className="mt-fib-5 text-[2.618rem] font-black leading-[1.02] tracking-tight sm:text-gr-3xl" data-testid="ambassador-title">
              Devenez ambassadeur Plan Permis
            </h1>
            <p className="mt-fib-4 measure text-gr-lg text-white/90" data-testid="ambassador-subtitle">
              Recommandez Plan Permis à des personnes qui cherchent une auto-école et gagnez une commission pour
              chaque inscription validée.
            </p>
            <ul className="mt-fib-5 space-y-fib-3 text-gr-sm text-white/90">
              <li className="flex items-center gap-fib-3"><IconCheck className="h-4 w-4 shrink-0" /> 25 € par filleul (particulier) · 50 € (professionnel)</li>
              <li className="flex items-center gap-fib-3"><IconCheck className="h-4 w-4 shrink-0" /> Paiement mensuel après inscription validée</li>
              <li className="flex items-center gap-fib-3"><IconCheck className="h-4 w-4 shrink-0" /> Inscription gratuite en 1 minute</li>
            </ul>
            <a
              href="#inscription"
              data-testid="hero-cta-btn"
              className="mt-fib-6 inline-block rounded-full bg-white px-fib-6 py-fib-4 text-gr-base font-bold text-brand transition-transform hover:scale-[1.02]"
            >
              Devenir ambassadeur
            </a>
          </div>
          <div>
            <CommissionCalculator showCta={false} />
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="px-6 py-fib-7 sm:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-gr-2xl font-extrabold tracking-tight text-neutral-900">Comment ça marche ?</h2>
          <div className="mt-fib-6 grid gap-fib-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-fib-5" data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-gr-xl font-black text-neutral-200">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-brand"><s.Icon className="h-8 w-8" /></span>
                </div>
                <h3 className="mt-fib-4 text-gr-lg font-bold text-neutral-900">{s.title}</h3>
                <p className="mt-fib-2 text-gr-sm text-neutral-500">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mx-auto mt-fib-6 max-w-3xl rounded-2xl bg-neutral-100 p-fib-5 text-gr-sm text-neutral-600" data-testid="ambassador-valid-client-terms">
            <p className="font-semibold text-neutral-800">Qu'est-ce qu'un client valide ? (conditions de commission)</p>
            <ul className="mt-fib-3 list-disc space-y-fib-2 pl-5">
              <li>Le filleul doit <strong>s'inscrire effectivement</strong> dans une auto-école partenaire du réseau.</li>
              <li>La commission n'est validée qu'<strong>après le délai légal de rétractation de 14 jours</strong> suivant l'inscription du filleul. En cas de rétractation dans ce délai, aucune commission n'est due.</li>
              <li>Les commissions sont validées après confirmation de l'inscription par notre équipe, puis payées <strong>mensuellement</strong>.</li>
              <li>Un candidat ne peut être attribué qu'à <strong>un seul ambassadeur</strong> (le 1er enregistré).</li>
              <li><strong>Pas d'auto-parrainage</strong> : vous ne pouvez pas vous parrainer vous-même (même email ou téléphone).</li>
              <li>Les <strong>doublons</strong> (candidat déjà connu par email ou téléphone) n'ouvrent aucun droit à commission.</li>
              <li>Toute fraude entraîne l'annulation des commissions et la suspension du code ambassadeur. Estimations non contractuelles.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Inscription / résultat */}
      <section id="inscription" className="px-6 pb-fib-8 sm:px-12">
        <div className="mx-auto max-w-xl">
          {result ? (
            <div className="rounded-3xl border border-emerald-200 bg-white p-fib-6 text-center shadow-lg" data-testid="ambassador-success">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <IconAmbSeal className="h-9 w-9" />
              </div>
              <h2 className="mt-fib-5 text-gr-xl font-extrabold text-neutral-900">Bienvenue {result.first_name} !</h2>
              <p className="mt-fib-2 text-neutral-500">Voici votre code ambassadeur unique. Partagez-le pour commencer à gagner.</p>
              <div className="mt-fib-5 flex items-center justify-center gap-fib-3">
                <span className="rounded-xl border-2 border-dashed border-brand bg-brand/5 px-fib-6 py-fib-3 text-gr-xl font-black tracking-wider text-brand" data-testid="ambassador-generated-code">
                  {result.code}
                </span>
                <button
                  onClick={copyCode}
                  data-testid="copy-code-btn"
                  className="inline-flex items-center gap-fib-2 rounded-full bg-brand px-fib-4 py-fib-3 text-gr-sm font-bold text-white transition-colors hover:bg-brand-hover"
                >
                  {copied ? <IconCheck className="h-4 w-4" /> : <IconCopy className="h-4 w-4" />}
                  {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <button
                onClick={() => navigate("/ambassadeur/dashboard", { state: { email: result.email, code: result.code } })}
                data-testid="goto-dashboard-btn"
                className="mt-fib-6 w-full rounded-full border border-neutral-300 px-fib-6 py-fib-4 text-gr-base font-bold text-neutral-800 transition-colors hover:border-brand hover:text-brand"
              >
                Accéder à mon tableau de bord
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-neutral-200 bg-white p-fib-6 shadow-lg" data-testid="ambassador-form">
              <h2 className="text-gr-xl font-extrabold tracking-tight text-neutral-900">Inscription ambassadeur</h2>
              <p className="mt-fib-2 text-gr-sm text-neutral-500">Gratuit et sans engagement. Votre code est généré immédiatement.</p>
              <div className="mt-fib-5">
                <span className="text-gr-sm font-semibold text-neutral-700">Vous êtes&nbsp;:</span>
                <div className="mt-fib-3 grid grid-cols-2 gap-fib-3">
                  {[
                    { v: "particulier", label: "Particulier", sub: "25 € / filleul" },
                    { v: "professionnel", label: "Professionnel", sub: "50 € / filleul" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      data-testid={`amb-type-${o.v}`}
                      onClick={() => set("type", o.v)}
                      className={`rounded-xl border px-fib-4 py-fib-3 text-left transition-colors ${
                        form.type === o.v ? "border-brand bg-brand/5" : "border-neutral-300 hover:border-neutral-400"
                      }`}
                    >
                      <span className={`block text-gr-sm font-bold ${form.type === o.v ? "text-brand" : "text-neutral-800"}`}>{o.label}</span>
                      <span className="block text-gr-xs text-neutral-500">{o.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-fib-5 grid gap-fib-4 sm:grid-cols-2">
                <input data-testid="amb-firstname-input" className={inputCls} placeholder="Prénom" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                <input data-testid="amb-lastname-input" className={inputCls} placeholder="Nom" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
              </div>
              <input data-testid="amb-email-input" type="email" className={`${inputCls} mt-fib-4`} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              <input data-testid="amb-phone-input" className={`${inputCls} mt-fib-4`} placeholder="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <textarea
                data-testid="amb-payment-input"
                className={`${inputCls} mt-fib-4`}
                rows={3}
                placeholder="Moyen de paiement / informations nécessaires (IBAN, PayPal, etc.)"
                value={form.payment_info}
                onChange={(e) => set("payment_info", e.target.value)}
              />
              <label className="mt-fib-4 flex items-start gap-fib-3 text-gr-sm text-neutral-600">
                <input
                  data-testid="amb-terms-checkbox"
                  type="checkbox"
                  checked={form.accepted_terms}
                  onChange={(e) => set("accepted_terms", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-brand"
                />
                <span>J'accepte les <Link to="/conditions-recompenses" className="font-semibold text-brand underline">conditions du programme ambassadeur</Link> Plan Permis (rémunération à l'inscription validée, paiement mensuel).</span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="amb-submit-btn"
                className="mt-fib-5 w-full rounded-full bg-brand px-fib-6 py-fib-4 text-gr-base font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
              >
                {loading ? "Création…" : "Obtenir mon code ambassadeur"}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
